"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.CollaborationUtil = exports.CollaborationConstants = void 0;
exports.listCollaborator = listCollaborator;
exports.checkPermission = checkPermission;
exports.grantPermission = grantPermission;
const tslib_1 = require("tslib");
const teamsfx_api_1 = require("@microsoft/teamsfx-api");
const axios_1 = tslib_1.__importDefault(require("axios"));
const dotenv = tslib_1.__importStar(require("dotenv"));
const fs_extra_1 = tslib_1.__importDefault(require("fs-extra"));
const uuid_1 = require("uuid");
const constants_1 = require("../common/constants");
const localizeUtils_1 = require("../common/localizeUtils");
const permissionInterface_1 = require("../common/permissionInterface");
const constants_2 = require("../component/constants");
const utils_1 = require("../component/driver/share/utils");
const collaboration_1 = require("../component/feature/collaboration");
const common_1 = require("../error/common");
const constants_3 = require("../question/constants");
class CollaborationConstants {
    // Collaboartion CLI parameters
    static TeamsAppId = "teamsAppId";
    static AadObjectId = "aadObjectId";
    static DotEnvFilePath = "dotEnvFilePath";
    // Collaboration env key
    static AadObjectIdEnv = "AAD_APP_OBJECT_ID";
    static TeamsAppIdEnv = "TEAMS_APP_ID";
    static TeamsAppTenantIdEnv = "TEAMS_APP_TENANT_ID";
    // App Type Question
    static AppType = "collaborationType";
    static TeamsAppQuestionId = "teamsApp";
    static AadAppQuestionId = "aadApp";
    static AgentOptionId = "agent";
    static placeholderRegex = /\$\{\{ *[a-zA-Z0-9_.-]* *\}\}/g;
}
exports.CollaborationConstants = CollaborationConstants;
class CollaborationUtil {
    static async getCurrentUserInfo(m365TokenProvider) {
        const user = await CollaborationUtil.getUserInfo(m365TokenProvider);
        if (!user) {
            return (0, teamsfx_api_1.err)(new teamsfx_api_1.SystemError(constants_2.SolutionSource, constants_2.SolutionError.FailedToRetrieveUserInfo, "Failed to retrieve current user info from graph token."));
        }
        return (0, teamsfx_api_1.ok)(user);
    }
    static async getUserInfo(m365TokenProvider, email) {
        const currentUserRes = await m365TokenProvider?.getJsonObject({ scopes: constants_1.GraphScopes });
        const currentUser = currentUserRes?.isOk() ? currentUserRes.value : undefined;
        if (!currentUser) {
            return undefined;
        }
        const tenantId = currentUser["tid"];
        let aadId = currentUser["oid"];
        let userPrincipalName = currentUser["unique_name"];
        let displayName = currentUser["name"];
        const isAdministrator = true;
        if (email) {
            const graphTokenRes = await m365TokenProvider?.getAccessToken({ scopes: constants_1.GraphScopes });
            const graphToken = graphTokenRes?.isOk() ? graphTokenRes.value : undefined;
            const instance = axios_1.default.create({
                baseURL: `${(0, constants_1.getResourceServiceEndpoint)(constants_1.ResourceServiceType.Graph)}/v1.0`,
            });
            instance.defaults.headers.common["Authorization"] = `Bearer ${graphToken}`;
            const res = await instance.get(`/users?$filter=startsWith(mail,'${email}') or startsWith(userPrincipalName, '${email}')`);
            if (!res || !res.data || !res.data.value) {
                return undefined;
            }
            const collaborator = res.data.value.find((user) => user.mail?.toLowerCase() === email.toLowerCase() ||
                user.userPrincipalName?.toLowerCase() === email.toLowerCase());
            if (!collaborator) {
                return undefined;
            }
            aadId = collaborator.id;
            userPrincipalName = collaborator.userPrincipalName;
            displayName = collaborator.displayName;
        }
        return {
            tenantId,
            aadId,
            userPrincipalName,
            displayName,
            isAdministrator,
        };
    }
    static async loadDotEnvFile(dotEnvFilePath) {
        try {
            const result = {};
            if (!(await fs_extra_1.default.pathExists(dotEnvFilePath))) {
                throw new common_1.FileNotFoundError("CollaboratorUtil", dotEnvFilePath);
            }
            const envs = dotenv.parse(await fs_extra_1.default.readFile(dotEnvFilePath));
            const entries = Object.entries(envs);
            for (const [key, value] of entries) {
                result[key] = value;
            }
            return (0, teamsfx_api_1.ok)(result);
        }
        catch (error) {
            return (0, teamsfx_api_1.err)(new teamsfx_api_1.UserError(constants_2.SolutionSource, constants_2.SolutionError.FailedToLoadDotEnvFile, (0, localizeUtils_1.getLocalizedString)("core.collaboration.error.failedToLoadDotEnvFile", error?.message)));
        }
    }
    // Priority parameter > dotenv > env
    static async getTeamsAppIdAndAadObjectId(inputs) {
        let teamsAppId, aadObjectId;
        // load from parameter and dotenv only wroks for cli
        if (inputs?.platform == teamsfx_api_1.Platform.CLI) {
            // 1. Get from parameter
            teamsAppId = inputs?.[CollaborationConstants.TeamsAppId] ?? undefined;
            aadObjectId = inputs?.[CollaborationConstants.AadObjectId] ?? undefined;
            // Return if getting two app ids
            if (teamsAppId && aadObjectId) {
                return (0, teamsfx_api_1.ok)({
                    teamsAppId: teamsAppId,
                    aadObjectId: aadObjectId,
                });
            }
            // 2. Get from dotenv
            if (inputs?.[CollaborationConstants.DotEnvFilePath]) {
                const loadDotEnvFileResult = await this.loadDotEnvFile(inputs?.[CollaborationConstants.DotEnvFilePath]);
                if (loadDotEnvFileResult.isErr()) {
                    return (0, teamsfx_api_1.err)(loadDotEnvFileResult.error);
                }
                const dotEnv = loadDotEnvFileResult.value;
                teamsAppId = teamsAppId ?? dotEnv[CollaborationConstants.TeamsAppIdEnv] ?? undefined;
                aadObjectId = aadObjectId ?? dotEnv[CollaborationConstants.AadObjectIdEnv] ?? undefined;
                // Return if getting two app ids
                if (teamsAppId && aadObjectId) {
                    return (0, teamsfx_api_1.ok)({
                        teamsAppId: teamsAppId,
                        aadObjectId: aadObjectId,
                    });
                }
            }
        }
        // 3. load from env
        const teamsAppManifestFilePath = inputs?.[constants_3.QuestionNames.TeamsAppManifestFilePath];
        const aadAppManifestFilePath = inputs?.[constants_3.QuestionNames.AadAppManifestFilePath];
        if (teamsAppManifestFilePath && !teamsAppId) {
            const teamsAppIdRes = await this.loadManifestId(teamsAppManifestFilePath);
            if (teamsAppIdRes.isOk()) {
                teamsAppId = this.parseManifestId(teamsAppIdRes.value);
                if (!teamsAppId) {
                    return (0, teamsfx_api_1.err)(new common_1.FailedToLoadManifestId(teamsAppManifestFilePath));
                }
            }
            else {
                return (0, teamsfx_api_1.err)(teamsAppIdRes.error);
            }
        }
        if (aadAppManifestFilePath && !aadObjectId) {
            const aadObjectIdRes = await this.loadManifestId(aadAppManifestFilePath);
            if (aadObjectIdRes.isOk()) {
                aadObjectId = this.parseManifestId(aadObjectIdRes.value);
                if (!aadObjectId) {
                    return (0, teamsfx_api_1.err)(new common_1.FailedToLoadManifestId(aadAppManifestFilePath));
                }
            }
            else {
                return (0, teamsfx_api_1.err)(aadObjectIdRes.error);
            }
        }
        return (0, teamsfx_api_1.ok)({
            teamsAppId: teamsAppId,
            aadObjectId: aadObjectId,
        });
    }
    static async loadManifestId(manifestFilePath) {
        try {
            if (!manifestFilePath || !(await fs_extra_1.default.pathExists(manifestFilePath))) {
                return (0, teamsfx_api_1.err)(new common_1.FileNotFoundError(constants_2.SolutionSource, manifestFilePath));
            }
            const manifest = await fs_extra_1.default.readJson(manifestFilePath);
            if (!manifest || !manifest.id) {
                return (0, teamsfx_api_1.err)(new teamsfx_api_1.UserError(constants_2.SolutionSource, constants_2.SolutionError.InvalidManifestError, (0, localizeUtils_1.getLocalizedString)("error.collaboration.InvalidManifestError", manifestFilePath)));
            }
            const id = manifest.id;
            return (0, teamsfx_api_1.ok)(id);
        }
        catch (error) {
            return (0, teamsfx_api_1.err)(new teamsfx_api_1.UserError(constants_2.SolutionSource, constants_2.SolutionError.FailedToLoadManifestFile, (0, localizeUtils_1.getLocalizedString)("error.collaboration.FailedToLoadManifest", manifestFilePath)));
        }
    }
    static requireEnvQuestion(appId) {
        return !!appId.match(CollaborationConstants.placeholderRegex);
    }
    static parseManifestId(appId) {
        // Hardcoded id in manifest
        if ((0, uuid_1.validate)(appId)) {
            return appId;
        }
        else if (appId.match(CollaborationConstants.placeholderRegex)) {
            // Reference value in .env file
            const envName = appId
                .replace(/\$*\{+/g, "")
                .replace(/\}+/g, "")
                .trim();
            return process.env[envName] ?? undefined;
        }
        return undefined;
    }
}
exports.CollaborationUtil = CollaborationUtil;
async function listCollaborator(ctx, inputs, tokenProvider, telemetryProps) {
    const result = await CollaborationUtil.getCurrentUserInfo(tokenProvider.m365TokenProvider);
    if (result.isErr()) {
        return (0, teamsfx_api_1.err)(result.error);
    }
    const user = result.value;
    const getAppIdsResult = await CollaborationUtil.getTeamsAppIdAndAadObjectId(inputs);
    if (getAppIdsResult.isErr()) {
        return (0, teamsfx_api_1.err)(getAppIdsResult.error);
    }
    const appIds = getAppIdsResult.value;
    let agentTitleId;
    if (inputs[constants_3.QuestionNames.collaborationAppType] &&
        inputs[constants_3.QuestionNames.collaborationAppType].indexOf(CollaborationConstants.AgentOptionId) > -1) {
        const parseRes = await (0, utils_1.parseShareAppActionYamlConfig)(inputs.projectPath);
        if (parseRes.isErr()) {
            return (0, teamsfx_api_1.err)(parseRes.error);
        }
        agentTitleId = parseRes.value.titleId;
    }
    const hasAad = appIds.aadObjectId != undefined;
    const hasTeams = appIds.teamsAppId != undefined;
    const teamsCollaboration = new collaboration_1.TeamsCollaboration(tokenProvider.m365TokenProvider);
    const aadCollaboration = new collaboration_1.AadCollaboration(tokenProvider.m365TokenProvider, ctx.logProvider);
    const agentCollaboration = new collaboration_1.AgentCollaboration(tokenProvider.m365TokenProvider);
    const appStudioRes = appIds.teamsAppId != undefined
        ? await teamsCollaboration.listCollaborator(ctx, appIds.teamsAppId)
        : (0, teamsfx_api_1.ok)([]);
    if (appStudioRes.isErr())
        return (0, teamsfx_api_1.err)(appStudioRes.error);
    const teamsAppOwners = appStudioRes.value;
    const aadRes = appIds.aadObjectId != undefined
        ? await aadCollaboration.listCollaborator(ctx, appIds.aadObjectId)
        : (0, teamsfx_api_1.ok)([]);
    if (aadRes.isErr())
        return (0, teamsfx_api_1.err)(aadRes.error);
    const aadOwners = aadRes.value;
    const teamsAppId = teamsAppOwners[0]?.resourceId ?? "";
    const aadAppId = aadOwners[0]?.resourceId ?? "";
    const aadAppTenantId = user.tenantId;
    const agentOwners = [];
    if (agentTitleId) {
        const result = await agentCollaboration.listCollaborator(ctx, agentTitleId);
        if (result.isErr())
            return (0, teamsfx_api_1.err)(result.error);
        agentOwners.push(...result.value);
    }
    if (inputs.platform === teamsfx_api_1.Platform.CLI || inputs.platform === teamsfx_api_1.Platform.VSCode) {
        const message = [
            {
                content: (0, localizeUtils_1.getLocalizedString)("core.collaboration.ListingM365Permission"),
                color: teamsfx_api_1.Colors.BRIGHT_WHITE,
            },
            {
                content: (0, localizeUtils_1.getLocalizedString)("core.collaboration.AccountUsedToCheck"),
                color: teamsfx_api_1.Colors.BRIGHT_WHITE,
            },
            { content: user.userPrincipalName + "\n", color: teamsfx_api_1.Colors.BRIGHT_MAGENTA },
            { content: (0, localizeUtils_1.getLocalizedString)("core.collaboration.TenantId"), color: teamsfx_api_1.Colors.BRIGHT_WHITE },
            { content: aadAppTenantId + "\n", color: teamsfx_api_1.Colors.BRIGHT_MAGENTA },
        ];
        if (hasTeams) {
            message.push(...getPrintEnvMessage(inputs.env, (0, localizeUtils_1.getLocalizedString)("core.collaboration.StartingListAllTeamsAppOwners")), {
                content: (0, localizeUtils_1.getLocalizedString)("core.collaboration.M365TeamsAppId"),
                color: teamsfx_api_1.Colors.BRIGHT_WHITE,
            }, { content: teamsAppId, color: teamsfx_api_1.Colors.BRIGHT_MAGENTA }, { content: `)\n`, color: teamsfx_api_1.Colors.BRIGHT_WHITE });
            for (const teamsAppOwner of teamsAppOwners) {
                message.push({
                    content: (0, localizeUtils_1.getLocalizedString)("core.collaboration.TeamsAppOwner"),
                    color: teamsfx_api_1.Colors.BRIGHT_WHITE,
                }, {
                    content: teamsAppOwner.userPrincipalName ?? teamsAppOwner.displayName,
                    color: teamsfx_api_1.Colors.BRIGHT_MAGENTA,
                }, { content: `.\n`, color: teamsfx_api_1.Colors.BRIGHT_WHITE });
            }
        }
        if (hasAad) {
            message.push(...getPrintEnvMessage(inputs.env, (0, localizeUtils_1.getLocalizedString)("core.collaboration.StartingListAllAadAppOwners")), {
                content: (0, localizeUtils_1.getLocalizedString)("core.collaboration.SsoAadAppId"),
                color: teamsfx_api_1.Colors.BRIGHT_WHITE,
            }, { content: aadAppId, color: teamsfx_api_1.Colors.BRIGHT_MAGENTA }, { content: `)\n`, color: teamsfx_api_1.Colors.BRIGHT_WHITE });
            for (const aadOwner of aadOwners) {
                message.push({
                    content: (0, localizeUtils_1.getLocalizedString)("core.collaboration.AadAppOwner"),
                    color: teamsfx_api_1.Colors.BRIGHT_WHITE,
                }, { content: aadOwner.userPrincipalName, color: teamsfx_api_1.Colors.BRIGHT_MAGENTA }, { content: `.\n`, color: teamsfx_api_1.Colors.BRIGHT_WHITE });
            }
        }
        if (agentTitleId) {
            message.push(...getPrintEnvMessage(inputs.env, (0, localizeUtils_1.getLocalizedString)("core.collaboration.StartingListAllAgentOwners")), {
                content: (0, localizeUtils_1.getLocalizedString)("core.collaboration.AgentTitleId"),
                color: teamsfx_api_1.Colors.BRIGHT_WHITE,
            }, { content: agentTitleId, color: teamsfx_api_1.Colors.BRIGHT_MAGENTA }, { content: `)\n`, color: teamsfx_api_1.Colors.BRIGHT_WHITE });
            for (const agentOwner of agentOwners) {
                message.push({
                    content: (0, localizeUtils_1.getLocalizedString)("core.collaboration.AgentOwner"),
                    color: teamsfx_api_1.Colors.BRIGHT_WHITE,
                }, {
                    content: agentOwner.userPrincipalName ?? agentOwner.displayName,
                    color: teamsfx_api_1.Colors.BRIGHT_MAGENTA,
                }, { content: `.\n`, color: teamsfx_api_1.Colors.BRIGHT_WHITE });
            }
        }
        if (inputs.platform === teamsfx_api_1.Platform.CLI) {
            void ctx.userInteraction.showMessage("info", message, false);
        }
        else if (inputs.platform === teamsfx_api_1.Platform.VSCode) {
            void ctx.userInteraction.showMessage("info", (0, localizeUtils_1.getLocalizedString)("core.collaboration.ListCollaboratorsSuccess", constants_1.VSCodeExtensionCommand.showOutputChannel), false);
            void ctx.logProvider.info(message);
        }
    }
    const aadOwnerCount = hasAad ? aadOwners.length : -1;
    const teamsOwnerCount = hasTeams ? teamsAppOwners.length : -1;
    if (telemetryProps) {
        telemetryProps[constants_2.SolutionTelemetryProperty.Env] = inputs.env || "";
        telemetryProps[constants_2.SolutionTelemetryProperty.CollaboratorCount] = teamsOwnerCount.toString();
        telemetryProps[constants_2.SolutionTelemetryProperty.AadOwnerCount] = aadOwnerCount.toString();
    }
    return (0, teamsfx_api_1.ok)({
        state: permissionInterface_1.CollaborationState.OK,
    });
}
async function checkPermission(ctx, inputs, tokenProvider, telemetryProps) {
    const result = await CollaborationUtil.getCurrentUserInfo(tokenProvider.m365TokenProvider);
    if (result.isErr()) {
        return (0, teamsfx_api_1.err)(result.error);
    }
    const userInfo = result.value;
    if (inputs.platform === teamsfx_api_1.Platform.CLI) {
        // TODO: get tenant id from .env
        const aadAppTenantId = userInfo.tenantId;
        const message = [
            {
                content: (0, localizeUtils_1.getLocalizedString)("core.collaboration.AccountUsedToCheck"),
                color: teamsfx_api_1.Colors.BRIGHT_WHITE,
            },
            { content: userInfo.userPrincipalName + "\n", color: teamsfx_api_1.Colors.BRIGHT_MAGENTA },
            ...getPrintEnvMessage(inputs.env, (0, localizeUtils_1.getLocalizedString)("core.collaboration.StaringCheckPermission")),
            { content: (0, localizeUtils_1.getLocalizedString)("core.collaboration.TenantId"), color: teamsfx_api_1.Colors.BRIGHT_WHITE },
            { content: aadAppTenantId + "\n", color: teamsfx_api_1.Colors.BRIGHT_MAGENTA },
        ];
        void ctx.userInteraction.showMessage("info", message, false);
    }
    const getAppIdsResult = await CollaborationUtil.getTeamsAppIdAndAadObjectId(inputs);
    if (getAppIdsResult.isErr()) {
        return (0, teamsfx_api_1.err)(getAppIdsResult.error);
    }
    const appIds = getAppIdsResult.value;
    const teamsCollaboration = new collaboration_1.TeamsCollaboration(tokenProvider.m365TokenProvider);
    const aadCollaboration = new collaboration_1.AadCollaboration(tokenProvider.m365TokenProvider, ctx.logProvider);
    const isTeamsActivated = appIds.teamsAppId != undefined;
    const appStudioRes = isTeamsActivated
        ? await teamsCollaboration.checkPermission(ctx, appIds.teamsAppId, userInfo)
        : (0, teamsfx_api_1.ok)([]);
    if (appStudioRes.isErr()) {
        return (0, teamsfx_api_1.err)(appStudioRes.error);
    }
    const permissions = appStudioRes.value;
    const isAadActivated = appIds.aadObjectId != undefined;
    if (isAadActivated) {
        const aadRes = await aadCollaboration.checkPermission(ctx, appIds.aadObjectId, result.value.aadId);
        if (aadRes.isErr())
            return (0, teamsfx_api_1.err)(aadRes.error);
        aadRes.value.forEach((r) => {
            permissions.push(r);
        });
    }
    if (inputs.platform === teamsfx_api_1.Platform.CLI) {
        for (const permission of permissions) {
            const message = [
                {
                    content: (0, localizeUtils_1.getLocalizedString)("core.collaboration.CheckPermissionResourceId"),
                    color: teamsfx_api_1.Colors.BRIGHT_WHITE,
                },
                {
                    content: permission.resourceId ?? (0, localizeUtils_1.getLocalizedString)("core.collaboration.Undefined"),
                    color: teamsfx_api_1.Colors.BRIGHT_MAGENTA,
                },
                {
                    content: (0, localizeUtils_1.getLocalizedString)("core.collaboration.ResourceName"),
                    color: teamsfx_api_1.Colors.BRIGHT_WHITE,
                },
                { content: permission.name, color: teamsfx_api_1.Colors.BRIGHT_MAGENTA },
                {
                    content: (0, localizeUtils_1.getLocalizedString)("core.collaboration.Permission"),
                    color: teamsfx_api_1.Colors.BRIGHT_WHITE,
                },
                {
                    content: permission.roles
                        ? permission.roles.toString()
                        : (0, localizeUtils_1.getLocalizedString)("core.collaboration.Undefined") + "\n",
                    color: teamsfx_api_1.Colors.BRIGHT_MAGENTA,
                },
            ];
            void ctx.userInteraction.showMessage("info", message, false);
        }
    }
    const aadPermission = permissions.find((permission) => permission.name === "Azure AD App");
    const teamsAppPermission = permissions.find((permission) => permission.name === "Teams App");
    if (telemetryProps) {
        telemetryProps[constants_2.SolutionTelemetryProperty.AadPermission] = aadPermission?.roles
            ? aadPermission.roles.join(";")
            : (0, localizeUtils_1.getLocalizedString)("core.collaboration.Undefined");
        telemetryProps[constants_2.SolutionTelemetryProperty.TeamsAppPermission] = teamsAppPermission?.roles
            ? teamsAppPermission.roles.join(";")
            : (0, localizeUtils_1.getLocalizedString)("core.collaboration.Undefined");
    }
    return (0, teamsfx_api_1.ok)({
        state: permissionInterface_1.CollaborationState.OK,
        permissions,
    });
}
async function grantPermission(ctx, inputs, tokenProvider) {
    const progressBar = ctx.userInteraction.createProgressBar((0, localizeUtils_1.getLocalizedString)("core.collaboration.GrantingPermission"), 1);
    try {
        const result = await CollaborationUtil.getCurrentUserInfo(tokenProvider.m365TokenProvider);
        if (result.isErr()) {
            return (0, teamsfx_api_1.err)(result.error);
        }
        const email = inputs.email;
        if (!email || email === result.value.userPrincipalName) {
            return (0, teamsfx_api_1.err)(new teamsfx_api_1.UserError("core", constants_2.SolutionError.EmailCannotBeEmptyOrSame, (0, localizeUtils_1.getDefaultString)("core.collaboration.EmailCannotBeEmptyOrSame"), (0, localizeUtils_1.getLocalizedString)("core.collaboration.EmailCannotBeEmptyOrSame")));
        }
        const userInfo = await CollaborationUtil.getUserInfo(tokenProvider.m365TokenProvider, email);
        if (!userInfo) {
            return (0, teamsfx_api_1.err)(new teamsfx_api_1.UserError("core", constants_2.SolutionError.CannotFindUserInCurrentTenant, (0, localizeUtils_1.getDefaultString)("core.collaboration.CannotFindUserInCurrentTenant"), (0, localizeUtils_1.getLocalizedString)("core.collaboration.CannotFindUserInCurrentTenant")));
        }
        await progressBar?.start();
        await progressBar?.next((0, localizeUtils_1.getLocalizedString)("core.collaboration.GrantPermissionForUser", email));
        const getAppIdsResult = await CollaborationUtil.getTeamsAppIdAndAadObjectId(inputs);
        if (getAppIdsResult.isErr()) {
            return (0, teamsfx_api_1.err)(getAppIdsResult.error);
        }
        const appIds = getAppIdsResult.value;
        let agentTitleId;
        if (inputs[constants_3.QuestionNames.collaborationAppType] &&
            inputs[constants_3.QuestionNames.collaborationAppType].indexOf(CollaborationConstants.AgentOptionId) > -1) {
            const parseRes = await (0, utils_1.parseShareAppActionYamlConfig)(inputs.projectPath);
            if (parseRes.isErr()) {
                return (0, teamsfx_api_1.err)(parseRes.error);
            }
            agentTitleId = parseRes.value.titleId;
        }
        if (inputs.platform === teamsfx_api_1.Platform.CLI) {
            // TODO: get tenant id from .env
            const aadAppTenantId = result.value.tenantId;
            const message = [
                {
                    content: (0, localizeUtils_1.getLocalizedString)("core.collaboration.AccountToGrantPermission"),
                    color: teamsfx_api_1.Colors.BRIGHT_WHITE,
                },
                { content: userInfo.userPrincipalName + "\n", color: teamsfx_api_1.Colors.BRIGHT_MAGENTA },
                ...getPrintEnvMessage(inputs.env, (0, localizeUtils_1.getLocalizedString)("core.collaboration.StartingGrantPermission")),
                { content: (0, localizeUtils_1.getLocalizedString)("core.collaboration.TenantId"), color: teamsfx_api_1.Colors.BRIGHT_WHITE },
                { content: aadAppTenantId + "\n", color: teamsfx_api_1.Colors.BRIGHT_MAGENTA },
            ];
            void ctx.userInteraction.showMessage("info", message, false);
        }
        const isAadActivated = appIds.aadObjectId != undefined;
        const isTeamsActivated = appIds.teamsAppId != undefined;
        const teamsCollaboration = new collaboration_1.TeamsCollaboration(tokenProvider.m365TokenProvider);
        const aadCollaboration = new collaboration_1.AadCollaboration(tokenProvider.m365TokenProvider, ctx.logProvider);
        const agentCollaboration = new collaboration_1.AgentCollaboration(tokenProvider.m365TokenProvider);
        const appStudioRes = isTeamsActivated
            ? await teamsCollaboration.grantPermission(ctx, appIds.teamsAppId, userInfo)
            : (0, teamsfx_api_1.ok)([]);
        if (appStudioRes.isErr()) {
            return (0, teamsfx_api_1.err)(appStudioRes.error);
        }
        const permissions = appStudioRes.value;
        if (isAadActivated) {
            const aadRes = await aadCollaboration.grantPermission(ctx, appIds.aadObjectId, userInfo.aadId);
            if (aadRes.isErr())
                return (0, teamsfx_api_1.err)(aadRes.error);
            aadRes.value.forEach((r) => {
                permissions.push(r);
            });
        }
        if (agentTitleId) {
            const result = await agentCollaboration.grantPermission(ctx, agentTitleId, userInfo);
            if (result.isErr())
                return (0, teamsfx_api_1.err)(result.error);
            result.value.forEach((r) => {
                permissions.push(r);
            });
        }
        if (inputs.platform === teamsfx_api_1.Platform.CLI) {
            for (const permission of permissions) {
                const message = [
                    { content: `${permission.roles?.join(",")} `, color: teamsfx_api_1.Colors.BRIGHT_MAGENTA },
                    {
                        content: (0, localizeUtils_1.getLocalizedString)("core.collaboration.PermissionHasBeenGrantTo"),
                        color: teamsfx_api_1.Colors.BRIGHT_WHITE,
                    },
                    { content: permission.name, color: teamsfx_api_1.Colors.BRIGHT_MAGENTA },
                    {
                        content: (0, localizeUtils_1.getLocalizedString)("core.collaboration.GrantPermissionResourceId"),
                        color: teamsfx_api_1.Colors.BRIGHT_WHITE,
                    },
                    { content: `${permission.resourceId}`, color: teamsfx_api_1.Colors.BRIGHT_MAGENTA },
                ];
                void ctx.userInteraction.showMessage("info", message, false);
            }
        }
        return (0, teamsfx_api_1.ok)({
            state: permissionInterface_1.CollaborationState.OK,
            userInfo: userInfo,
            permissions,
        });
    }
    finally {
        await progressBar?.end(true);
    }
}
function getPrintEnvMessage(env, message) {
    return env
        ? [
            {
                content: message,
                color: teamsfx_api_1.Colors.BRIGHT_WHITE,
            },
            { content: `${env}\n`, color: teamsfx_api_1.Colors.BRIGHT_MAGENTA },
        ]
        : [];
}
//# sourceMappingURL=collaborator.js.map